/**
 * PursuitNextStepFooter — v3.18.10
 *
 * Lifecycle-aware "Next →" footer for every pursuit page. Reads the
 * pursuit's current notice_type from PursuitContext (or fetches if the
 * caller didn't supply it) and renders a chevron CTA to the next logical
 * stage in the federal capture lifecycle:
 *
 *   intake → ingestion → (RFI: capability_statement) → agency_questions →
 *   gonogo → gap-analysis → strategy → proposal-workspace → compliance →
 *   pipeline (terminal)
 *
 * Each page imports this with a single `currentStep` prop. The footer is
 * defensive: if the route can't be computed (missing workspaceId, missing
 * pursuit, terminal stage), it renders nothing.
 *
 * NO backend changes. NO routing changes. Pure UX glue.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowRight, CheckCircle2, KanbanSquare } from 'lucide-react';
import { Button } from '../ui/button';
import { useAuth } from '../../contexts/AuthContext';
import { usePursuit } from '../../contexts/PursuitContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Single source of truth for "what comes next". `nextFor(notice_type)`
// lets RFI pursuits dive into Capability Statement right after Ingestion.
// v3.18.16 — Compliance split: Strategy → Compliance Matrix → Proposal
// → Submission → pipeline.
const LIFECYCLE = {
  intake: {
    label: 'Opportunity Intake',
    next: (_nt) => 'ingestion',
  },
  ingestion: {
    label: 'Document Ingestion',
    next: (nt) => (nt === 'RFI' ? 'capability_statement' : 'agency_questions'),
  },
  capability_statement: {
    label: 'Capability Statement',
    next: (_nt) => 'agency_questions',
  },
  agency_questions: {
    label: 'Questions for Agency',
    next: (_nt) => 'gonogo',
  },
  gonogo: {
    label: 'Go/No-Go Dashboard',
    next: (_nt) => 'gaps',
  },
  gaps: {
    label: 'Gap Analysis & Teaming',
    next: (_nt) => 'strategy',
  },
  strategy: {
    label: 'Strategy',
    next: (_nt) => 'compliance_matrix',
  },
  compliance_matrix: {
    label: 'Compliance Check',
    next: (_nt) => 'proposal',
  },
  proposal: {
    label: 'Proposal Workspace',
    next: (_nt) => 'submission',
  },
  submission: {
    label: 'Submission',
    next: (_nt) => 'pipeline', // terminal — back to the master board
  },
};

// menu-id → URL path segment. Uses canonical route names (App.js maps
// legacy aliases like 'gap-analysis' → 'gaps' and 'proposal-workspace' →
// 'proposal' via redirects; we go straight to canonical here to avoid the
// extra hop.) Both 'compliance_matrix' and 'submission' route to the same
// page on different tabs.
const PATH_BY_ID = {
  intake:               'intake',
  ingestion:            'ingestion',
  capability_statement: 'capability-statement',
  agency_questions:     'questions',
  gonogo:               'gonogo',
  gaps:                 'gaps',
  strategy:             'strategy',
  compliance_matrix:    'compliance',
  proposal:             'proposal',
  submission:           'compliance?tab=submission-day',
};

// Human labels shown in the CTA button.
const LABEL_BY_ID = {
  intake:               'Opportunity Intake',
  ingestion:            'Document Ingestion',
  capability_statement: 'Capability Statement',
  agency_questions:     'Questions for Agency',
  gonogo:               'Go/No-Go Dashboard',
  gaps:                 'Gap Analysis & Teaming',
  strategy:             'Strategy',
  compliance_matrix:    'Compliance Check',
  proposal:             'Proposal Workspace',
  submission:           'Submission',
};

export default function PursuitNextStepFooter({ currentStep }) {
  const navigate = useNavigate();
  const { workspaceId, pursuitId } = useParams();
  const { authToken } = useAuth();
  const { activePursuit } = usePursuit();
  const [noticeType, setNoticeType] = useState(activePursuit?.notice_type || null);

  // Resolve notice_type once when missing. Most callers pass it via
  // enterPursuit() already; this fetch is the safety net.
  useEffect(() => {
    if (noticeType || !pursuitId || !authToken) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/pursuits/${pursuitId}`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (!cancelled) setNoticeType(data?.notice_type || null);
      } catch (_) { /* silent — footer just falls back to non-RFI path */ }
    })();
    return () => { cancelled = true; };
  }, [pursuitId, authToken, noticeType]);

  if (!workspaceId || !pursuitId) return null;
  const node = LIFECYCLE[currentStep];
  if (!node) return null;
  const nextId = node.next(noticeType);
  if (!nextId) return null;

  // Terminal → "Back to Pipeline" button instead of "Next →"
  const isTerminal = nextId === 'pipeline';
  const targetPath = isTerminal
    ? `/${workspaceId}/pipeline`
    : `/${workspaceId}/pursuit/${pursuitId}/${PATH_BY_ID[nextId]}`;

  return (
    <div
      className="mt-10 px-6 lg:px-8 max-w-7xl mx-auto"
      data-testid={`pursuit-next-step-footer-${currentStep}`}
    >
      <div className="border-t border-slate-200 pt-5 pb-2 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="text-xs text-slate-500 flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
          <span>
            You're on <strong className="text-slate-700">{node.label}</strong>
            {isTerminal
              ? ' — submission ready.'
              : <> · next up: <strong className="text-slate-700">{LABEL_BY_ID[nextId]}</strong></>}
          </span>
        </div>
        <Button
          onClick={() => navigate(targetPath)}
          className={
            isTerminal
              ? 'bg-slate-700 hover:bg-slate-800 text-white'
              : 'bg-emerald-600 hover:bg-emerald-700 text-white'
          }
          data-testid={`pursuit-next-step-cta-${currentStep}`}
        >
          {isTerminal ? (
            <>
              <KanbanSquare className="w-4 h-4 mr-1.5" />
              Back to Pipeline
            </>
          ) : (
            <>
              Continue to {LABEL_BY_ID[nextId]}
              <ArrowRight className="w-4 h-4 ml-1.5" />
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
